import { formatDisplayDate } from './dateDisplay';

export const DASHA_SEQUENCE = [
  { planet: 'Ketu', years: 7 },
  { planet: 'Venus', years: 20 },
  { planet: 'Sun', years: 6 },
  { planet: 'Moon', years: 10 },
  { planet: 'Mars', years: 7 },
  { planet: 'Rahu', years: 18 },
  { planet: 'Jupiter', years: 16 },
  { planet: 'Saturn', years: 19 },
  { planet: 'Mercury', years: 17 }
];

const TOTAL_CYCLE_YEARS = 120;
const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;
const NAKSHATRA_SPAN_DEGREES = 360 / 27;

export type DashaLevel = 'Mahadasha' | 'Antardasha' | 'Pratyantardasha' | 'Sookshma' | 'Prana';

export interface DashaEntry {
  level: DashaLevel;
  lord: string;
  periodYears: number;
  start: Date;
  end: Date;
  sequence: number;
  antardashas?: DashaEntry[];
  pratyantardashas?: DashaEntry[];
  sookshmaDashas?: DashaEntry[];
  pranaDashas?: DashaEntry[];
}

export interface DashaCycleInfo {
  cycleNumber: number;
  currentCycle: number;
  cycleStart: Date;
  cycleEnd: Date;
  showCycleSelector: boolean;
}

export interface RuntimeDashaResult extends DashaCycleInfo {
  birthMahadashaLord: string;
  balanceYears: number;
  timezoneMinutes: number;
  mahadashas: DashaEntry[];
  currentMahadasha: DashaEntry | null;
  currentAntardasha: DashaEntry | null;
  currentPratyantardasha: DashaEntry | null;
  currentSookshmaDasha: DashaEntry | null;
  currentPranaDasha: DashaEntry | null;
}

const normalizeLongitude = (longitude: number) => ((longitude % 360) + 360) % 360;

const getPlanetYears = (planet: string) => {
  return DASHA_SEQUENCE.find(entry => entry.planet === planet)?.years || 0;
};

const rotateSequence = (startPlanet: string) => {
  const startIndex = DASHA_SEQUENCE.findIndex(entry => entry.planet === startPlanet);
  if (startIndex < 0) return DASHA_SEQUENCE;

  return [
    ...DASHA_SEQUENCE.slice(startIndex),
    ...DASHA_SEQUENCE.slice(0, startIndex)
  ];
};

export const calculateDashaSubPeriods = (parent: DashaEntry, level: 'Sookshma' | 'Prana'): DashaEntry[] => {
  let periodStart = new Date(parent.start);
  return rotateSequence(parent.lord).map((planet, index) => {
    const periodYears = (parent.periodYears * planet.years) / TOTAL_CYCLE_YEARS;
    const periodEnd = index === DASHA_SEQUENCE.length - 1
      ? new Date(parent.end)
      : new Date(periodStart.getTime() + (periodYears * YEAR_MS));
    const entry: DashaEntry = { level, lord: planet.planet, periodYears, start: new Date(periodStart), end: periodEnd, sequence: index + 1 };
    periodStart = periodEnd;
    return entry;
  });
};

const findPeriodAt = (periods: DashaEntry[], asOfDate: Date) => periods.find((entry) => asOfDate >= entry.start && asOfDate < entry.end) || null;

export const parseTimezoneOffsetMinutes = (timezone: unknown): number => {
  if (typeof timezone === 'number' && Number.isFinite(timezone)) {
    return timezone;
  }

  if (typeof timezone === 'string') {
    const trimmed = timezone.trim();

    if (/^[+-]?\d+$/.test(trimmed)) {
      return Number(trimmed);
    }

    const match = trimmed.match(/^([+-]?)(\d{1,2}):(\d{2})$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);
      return sign * ((hours * 60) + minutes);
    }
  }

  return 0;
};

const getBirthInstant = (chartData: any): Date | null => {
  const birthDateValue = chartData?.birthDetails?.dateOfBirth;
  const birthDate = new Date(birthDateValue);

  if (!birthDateValue || Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const timeOfBirth = chartData?.birthDetails?.timeOfBirth || {};
  const timezoneMinutes = parseTimezoneOffsetMinutes(chartData?.birthDetails?.placeOfBirth?.timezone);

  const utcMillis = Date.UTC(
    birthDate.getUTCFullYear(),
    birthDate.getUTCMonth(),
    birthDate.getUTCDate(),
    Number(timeOfBirth.hour) || 0,
    Number(timeOfBirth.minute) || 0,
    Number(timeOfBirth.second) || 0
  ) - (timezoneMinutes * 60 * 1000);

  return new Date(utcMillis);
};

const getDashaBirthAnchor = (chartData: any) => {
  const moonLongitude = chartData?.planets?.MOON?.longitude;
  const birthInstant = getBirthInstant(chartData);
  const timezoneMinutes = parseTimezoneOffsetMinutes(chartData?.birthDetails?.placeOfBirth?.timezone);

  if (typeof moonLongitude !== 'number' || !Number.isFinite(moonLongitude) || !birthInstant) {
    return null;
  }

  const normalizedMoon = normalizeLongitude(moonLongitude);
  const nakshatraIndex = Math.floor(normalizedMoon / NAKSHATRA_SPAN_DEGREES);
  const birthMahadashaLord = DASHA_SEQUENCE[nakshatraIndex % DASHA_SEQUENCE.length].planet;
  const birthMahadashaYears = getPlanetYears(birthMahadashaLord);

  const degreesTraversedInNakshatra = normalizedMoon - (nakshatraIndex * NAKSHATRA_SPAN_DEGREES);
  const degreesRemainingInNakshatra = NAKSHATRA_SPAN_DEGREES - degreesTraversedInNakshatra;
  const remainingFraction = degreesRemainingInNakshatra / NAKSHATRA_SPAN_DEGREES;
  const balanceYears = birthMahadashaYears * remainingFraction;
  const elapsedYears = birthMahadashaYears - balanceYears;

  const anchorStart = new Date(birthInstant.getTime() - (elapsedYears * YEAR_MS));
  return { birthInstant, anchorStart, birthMahadashaLord, balanceYears, timezoneMinutes };
};

/** Cycle 1 begins at the full birth Mahadasha start, including its elapsed balance. */
export const getDashaCycleInfo = (chartData: any, asOfDate: Date = new Date(), selectedCycle?: number): DashaCycleInfo | null => {
  const anchor = getDashaBirthAnchor(chartData);
  if (!anchor || !Number.isFinite(asOfDate.getTime())) return null;
  const cycleMs = TOTAL_CYCLE_YEARS * YEAR_MS;
  const currentCycle = Math.max(1, Math.floor((asOfDate.getTime() - anchor.anchorStart.getTime()) / cycleMs) + 1);
  const cycleNumber = selectedCycle ?? currentCycle;
  if (!Number.isSafeInteger(cycleNumber) || cycleNumber < 1) return null;
  const cycleStart = new Date(anchor.anchorStart.getTime() + (cycleNumber - 1) * cycleMs);
  const cycleEnd = new Date(cycleStart.getTime() + cycleMs);
  if (!Number.isFinite(cycleStart.getTime()) || !Number.isFinite(cycleEnd.getTime())) return null;
  return {
    cycleNumber, currentCycle, cycleStart, cycleEnd,
    showCycleSelector: asOfDate.getTime() - anchor.birthInstant.getTime() > cycleMs,
  };
};

export const calculateRuntimeDasha = (chartData: any, asOfDate: Date = new Date(), selectedCycle?: number): RuntimeDashaResult | null => {
  const anchor = getDashaBirthAnchor(chartData);
  const cycle = getDashaCycleInfo(chartData, asOfDate, selectedCycle);
  if (!anchor || !cycle) return null;
  const { birthMahadashaLord, balanceYears, timezoneMinutes } = anchor;
  let mahadashaStart = new Date(cycle.cycleStart);

  const mahadashas: DashaEntry[] = [];
  let currentMahadasha: DashaEntry | null = null;
  let currentAntardasha: DashaEntry | null = null;
  let currentPratyantardasha: DashaEntry | null = null;
  let currentSookshmaDasha: DashaEntry | null = null;
  let currentPranaDasha: DashaEntry | null = null;

  rotateSequence(birthMahadashaLord).forEach((mahadasha, mdIndex) => {
    const mdYears = mahadasha.years;
    const mdEnd = new Date(mahadashaStart.getTime() + (mdYears * YEAR_MS));

    const mahadashaEntry: DashaEntry = {
      level: 'Mahadasha',
      lord: mahadasha.planet,
      periodYears: mdYears,
      start: new Date(mahadashaStart),
      end: mdEnd,
      sequence: mdIndex + 1,
      antardashas: []
    };

    let antardashaStart = new Date(mahadashaStart);

    rotateSequence(mahadasha.planet).forEach((antardasha, adIndex) => {
      const adYears = (mdYears * antardasha.years) / TOTAL_CYCLE_YEARS;
      const adEnd = adIndex === DASHA_SEQUENCE.length - 1
        ? new Date(mdEnd)
        : new Date(antardashaStart.getTime() + (adYears * YEAR_MS));

      const antardashaEntry: DashaEntry = {
        level: 'Antardasha',
        lord: antardasha.planet,
        periodYears: adYears,
        start: new Date(antardashaStart),
        end: adEnd,
        sequence: adIndex + 1,
        pratyantardashas: []
      };

      let pratyantardashaStart = new Date(antardashaStart);

      rotateSequence(antardasha.planet).forEach((pratyantardasha, pdIndex) => {
        const pdYears = (mdYears * antardasha.years * pratyantardasha.years) / (TOTAL_CYCLE_YEARS * TOTAL_CYCLE_YEARS);
        const pdEnd = pdIndex === DASHA_SEQUENCE.length - 1
          ? new Date(adEnd)
          : new Date(pratyantardashaStart.getTime() + (pdYears * YEAR_MS));

        const pratyantardashaEntry: DashaEntry = {
          level: 'Pratyantardasha',
          lord: pratyantardasha.planet,
          periodYears: pdYears,
          start: new Date(pratyantardashaStart),
          end: pdEnd,
          sequence: pdIndex + 1
        };

        if (asOfDate >= pratyantardashaEntry.start && asOfDate < pratyantardashaEntry.end) {
          currentPratyantardasha = pratyantardashaEntry;
        }

        antardashaEntry.pratyantardashas?.push(pratyantardashaEntry);
        pratyantardashaStart = pdEnd;
      });

      if (asOfDate >= antardashaEntry.start && asOfDate < antardashaEntry.end) {
        currentAntardasha = antardashaEntry;
      }

      mahadashaEntry.antardashas?.push(antardashaEntry);
      antardashaStart = adEnd;
    });

    if (asOfDate >= mahadashaEntry.start && asOfDate < mahadashaEntry.end) {
      currentMahadasha = mahadashaEntry;
    }

    mahadashas.push(mahadashaEntry);
    mahadashaStart = mdEnd;
  });

  const activePratyantardasha = currentPratyantardasha as DashaEntry | null;
  if (activePratyantardasha) {
    const sookshmaDashas = calculateDashaSubPeriods(activePratyantardasha, 'Sookshma');
    activePratyantardasha.sookshmaDashas = sookshmaDashas;
    currentSookshmaDasha = findPeriodAt(sookshmaDashas, asOfDate);
    if (currentSookshmaDasha) {
      const pranaDashas = calculateDashaSubPeriods(currentSookshmaDasha, 'Prana');
      currentSookshmaDasha.pranaDashas = pranaDashas;
      currentPranaDasha = findPeriodAt(pranaDashas, asOfDate);
    }
  }

  return {
    ...cycle,
    birthMahadashaLord,
    balanceYears,
    timezoneMinutes,
    mahadashas,
    currentMahadasha,
    currentAntardasha,
    currentPratyantardasha,
    currentSookshmaDasha,
    currentPranaDasha
  };
};

const shiftToTimezone = (date: Date, timezoneMinutes: number) => {
  return new Date(date.getTime() + (timezoneMinutes * 60 * 1000));
};

export const formatDashaDate = (date: Date, timezoneMinutes = 0) => {
  return formatDisplayDate(shiftToTimezone(date, timezoneMinutes), { timeZone: 'UTC' });
};

export const formatDashaTime = (date: Date, timezoneMinutes = 0) => {
  return shiftToTimezone(date, timezoneMinutes).toLocaleTimeString('en-GB', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const formatDashaYears = (periodYears: number) => {
  const totalDays = periodYears * 365.25;
  const years = Math.floor(totalDays / 365.25);
  const remainingDaysAfterYears = totalDays % 365.25;
  const months = Math.floor(remainingDaysAfterYears / 30.4375);
  const days = Math.round(remainingDaysAfterYears % 30.4375);

  if (years > 0) return `${years}y ${months}m ${days}d`;
  if (months > 0) return `${months}m ${days}d`;
  return `${days}d`;
};
